import React, { useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { REPORT_TYPES, getRemoteRuntime, report } from '@adsk/exo-skeleton-api';
import {
  safeStringify,
  toError,
  wrapWithEventPropertiesPrefix,
} from '@adsk/exo-skeleton-api/internal';

import {
  EXO_SKELETON_SCOPE_NAME,
  REMOTE_MODULE_ERROR,
} from '../helpers/consts';
import { RemoteProvider, useRemote } from '../providers/RemoteProvider';
import { RemoteComponentProps } from '../types';

export type { RemoteComponentProps } from '../types';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const RemoteWrapper = <PROPS extends {} = {}>({
  children,
  ...props
}: RemoteComponentProps<PROPS>) => {
  const { moduleName, scope, renderError, version } = props;
  const { moniker, override } = getRemoteRuntime(scope) || {};

  const parentRemoteValue = useRemote();

  const childrenToRender = useMemo(() => {
    if (!children) return null;

    return parentRemoteValue ? (
      <RemoteProvider {...parentRemoteValue}>{children}</RemoteProvider>
    ) : (
      children
    );
  }, [children, parentRemoteValue]);

  return (
    <ErrorBoundary
      fallbackRender={({ error, ...fallbackRest }) =>
        renderError?.({ ...fallbackRest, error: toError(error) })
      }
      resetKeys={[moniker, override, ...Object.values(props)]}
      onError={(rawError) => {
        const error = toError(rawError);
        const unsafeGlobalContext = {
          type: REMOTE_MODULE_ERROR,
          moniker,
          override,
          scope,
          moduleName,
          module: moduleName,
          version,
        };

        return report({
          scope: EXO_SKELETON_SCOPE_NAME,
          type: REPORT_TYPES.error,
          value: error,
          context: {
            ...wrapWithEventPropertiesPrefix({
              data: safeStringify(unsafeGlobalContext),
              message: error?.message,
              stack: error?.stack,
              type: REPORT_TYPES.error,
            }),
          },
          UNSAFE__globalContext: { ...unsafeGlobalContext },
        });
      }}
    >
      <RemoteProvider scope={scope} moduleName={moduleName}>
        {childrenToRender}
      </RemoteProvider>
    </ErrorBoundary>
  );
};
